/**
 * The radar's status palette, kept out of `radarUi.tsx` so that file exports only components and
 * keeps fast refresh working.
 *
 * ── Why this tab DOES use the status hues that Role Prioritisation gave up ────────────────────
 * Role Prioritisation encodes AAA→C as an ordinal green→red ramp, which claims the three status
 * colours, so that tab carries status structurally instead (Trusted Blue, badges, a coloured rule).
 * The radar has no ordinal scale: green/orange/red here mean exactly what the design system says
 * they mean — on track, at risk, off. That is the same three colours doing a different job in a
 * different tab, not a contradiction.
 *
 * It does mean ONE thing must not be carried over. A role's AAA/A/B/C rating renders here as a
 * plain monochrome chip, never as `RatingBadge`. A green "AAA" pill beside a red "Off" pill on the
 * same card is two colour systems disagreeing inside one row, and the reader has no way to know
 * which one the colour belongs to. The letters still identify the priority; the colour stays with
 * the traffic light.
 *
 * The dark text tones are the same documented deviation `prioritisationUi.tsx` takes for
 * `FactStrip`: the design system ships soft tints and no readable dark partner for them, so each
 * soft background gets a hand-picked foreground that clears contrast on it.
 */
import type { StageStatus } from '../../../utils/bottleneckRadar';

export interface StatusStyle {
  /** Solid fill, for dots. */
  dot: string;
  /** Soft tint plus a foreground that reads on it, for pills. */
  pill: string;
  /** The soft tint alone, for tinting the bottleneck's own phase card. */
  tint: string;
  /** Foreground alone, for a figure sitting on white. */
  text: string;
  label: string;
}

export const STATUS: Record<StageStatus, StatusStyle> = {
  green: {
    dot: 'bg-green',
    pill: 'bg-lightGreen text-[#0F6B34]',
    tint: 'bg-lightGreen',
    text: 'text-[#0F6B34]',
    label: 'On track',
  },
  yellow: {
    dot: 'bg-orange',
    pill: 'bg-lightOrange text-[#7A4A12]',
    tint: 'bg-lightOrange',
    text: 'text-[#7A4A12]',
    label: 'At risk',
  },
  red: {
    dot: 'bg-red',
    pill: 'bg-lightRed text-[#8A0E22]',
    tint: 'bg-lightRed',
    text: 'text-[#8A0E22]',
    label: 'Off',
  },
  unknown: {
    dot: 'bg-pewter',
    pill: 'bg-cotton text-iron',
    tint: 'bg-cotton',
    text: 'text-steel',
    label: 'Not enough data',
  },
};

export const DOT_SIZE = {
  sm: 'h-[7px] w-[7px]',
  md: 'h-2.5 w-2.5',
  lg: 'h-[11px] w-[11px]',
} as const;
